import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import SkeletonWrapper from './SkeletonWrapper';
import useMobile from 'services/Hooks/useMobile';

const MAGNIFIER_POSITION_DELTA = 0.75;
const MAGNIFIER_LENS_OVERFLOW = 24;

const useStyles = makeStyles(theme => ({
  selectedImg: {
    borderStyle: 'solid',
    border: `1px ${theme.palette.secondary.main}`,
    width: 84,
    height: 84,
    marginRight: '1vh',
    objectFit: 'cover',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  imgPreview: {
    borderStyle: 'solid',
    border: '1px black',
    width: 84,
    height: 84,
    marginRight: '1vh',
    objectFit: 'cover',
    boxSizing: 'border-box',
    '&:hover': {
      border: '1px gray',
      cursor: 'pointer',
    },
  },
}));

const ImagesSlideView = ({
  images,
  getImageUrl,
  isLoading,
  onUpdateImage,
  defaultImage,
  mainImageContainerSx,
  thumbnailContainerSx,
  mainImageStyle,
  enableMagnifier,
  magnifierZoom = 2.2,
  magnifierSize = 160,
}) => {
  const classes = useStyles();
  const [selectedImg, setSelectedImg] = useState();
  const magnifierFrameRef = useRef(null);
  const latestMagnifierPayloadRef = useRef(null);
  const [magnifierState, setMagnifierState] = useState({
    isVisible: false,
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const isMobile = useMobile();
  const selectedImageId = selectedImg?.id || images?.find(i => i.dealerPortalPrimary)?.id || images?.[0]?.id;
  const mainImageSrc =
    images?.length > 0 ? getImageUrl(selectedImageId) : defaultImage ? defaultImage : '/images/brp-logo.png';

  const handleUpdateImage = useCallback(
    img => {
      setSelectedImg(img);

      if (onUpdateImage) {
        onUpdateImage(img);
      }
    },
    [onUpdateImage],
  );

  useEffect(() => {
    if (images?.length > 0) {
      const currentSelection = selectedImg?.id ? images.find(i => i.id === selectedImg.id) : null;
      if (currentSelection) {
        return;
      }

      handleUpdateImage(images.find(i => i.dealerPortalPrimary) ?? images[0]);
    } else {
      handleUpdateImage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUpdateImage, images, selectedImg?.id]);

  const shouldShowMagnifier = enableMagnifier && !isMobile;

  useEffect(
    () => () => {
      if (magnifierFrameRef.current) {
        cancelAnimationFrame(magnifierFrameRef.current);
      }
    },
    [],
  );

  const flushMagnifierFrame = useCallback(() => {
    magnifierFrameRef.current = null;

    if (!latestMagnifierPayloadRef.current) {
      return;
    }

    const next = latestMagnifierPayloadRef.current;
    latestMagnifierPayloadRef.current = null;

    setMagnifierState(current => {
      const isNearSamePoint =
        Math.abs(current.x - next.x) < MAGNIFIER_POSITION_DELTA &&
        Math.abs(current.y - next.y) < MAGNIFIER_POSITION_DELTA &&
        current.width === next.width &&
        current.height === next.height &&
        current.isVisible === next.isVisible;

      return isNearSamePoint ? current : next;
    });

    if (latestMagnifierPayloadRef.current && !magnifierFrameRef.current) {
      magnifierFrameRef.current = requestAnimationFrame(flushMagnifierFrame);
    }
  }, []);

  const handleMagnifierMove = e => {
    if (!shouldShowMagnifier) {
      return;
    }

    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max(e.clientX - left, 0), width);
    const y = Math.min(Math.max(e.clientY - top, 0), height);

    latestMagnifierPayloadRef.current = {
      isVisible: true,
      x,
      y,
      width,
      height,
    };

    if (!magnifierFrameRef.current) {
      magnifierFrameRef.current = requestAnimationFrame(flushMagnifierFrame);
    }
  };

  const hideMagnifier = () => {
    latestMagnifierPayloadRef.current = null;

    if (magnifierFrameRef.current) {
      cancelAnimationFrame(magnifierFrameRef.current);
      magnifierFrameRef.current = null;
    }

    setMagnifierState(current => ({ ...current, isVisible: false }));
  };

  const lensOverflow = Math.min(MAGNIFIER_LENS_OVERFLOW, magnifierSize / 2);
  const lensLeft = Math.min(
    Math.max(magnifierState.x - magnifierSize / 2, -lensOverflow),
    Math.max(magnifierState.width - magnifierSize + lensOverflow, -lensOverflow),
  );
  const lensTop = Math.min(
    Math.max(magnifierState.y - magnifierSize / 2, -lensOverflow),
    Math.max(magnifierState.height - magnifierSize + lensOverflow, -lensOverflow),
  );
  const sampleHalfWindow = magnifierSize / (2 * magnifierZoom);
  const sampleMinX = Math.min(sampleHalfWindow, magnifierState.width / 2);
  const sampleMaxX = Math.max(magnifierState.width - sampleHalfWindow, sampleMinX);
  const sampleMinY = Math.min(sampleHalfWindow, magnifierState.height / 2);
  const sampleMaxY = Math.max(magnifierState.height - sampleHalfWindow, sampleMinY);
  const clampedSampleX = Math.min(Math.max(magnifierState.x, sampleMinX), sampleMaxX);
  const clampedSampleY = Math.min(Math.max(magnifierState.y, sampleMinY), sampleMaxY);

  return (
    <SkeletonWrapper
      isLoading={isLoading}
      fullWidth
      variant="rectangular"
      component={
        <>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ ml: 20, mr: 20, ...(mainImageContainerSx || {}) }}>
            <Box
              sx={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}
              onMouseMove={handleMagnifierMove}
              onMouseLeave={hideMagnifier}>
              <img
                src={mainImageSrc}
                alt="Header"
                style={
                  isMobile
                    ? {
                        maxHeight: '65vh',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        cursor: shouldShowMagnifier ? 'zoom-in' : 'default',
                        ...(mainImageStyle || {}),
                      }
                    : {
                        height: '65vh',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        cursor: shouldShowMagnifier ? 'zoom-in' : 'default',
                        ...(mainImageStyle || {}),
                      }
                }
              />

              {shouldShowMagnifier && magnifierState.isVisible && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: lensLeft,
                    top: lensTop,
                    width: magnifierSize,
                    height: magnifierSize,
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
                    pointerEvents: 'none',
                    backgroundImage: `url(${mainImageSrc})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundColor: '#fff',
                    backgroundSize: `${magnifierState.width * magnifierZoom}px ${magnifierState.height * magnifierZoom}px`,
                    backgroundPosition: `-${clampedSampleX * magnifierZoom - magnifierSize / 2}px -${clampedSampleY *
                      magnifierZoom -
                      magnifierSize / 2}px`,
                  }}
                />
              )}
            </Box>
          </Box>

          {images?.length > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ ml: 20, mr: 20, mt: 2, flexWrap: 'wrap', ...(thumbnailContainerSx || {}) }}>
              {images?.map(img => (
                <Box key={img?.id} sx={{ mb: 1 }}>
                  <img
                    className={selectedImg?.id === img?.id ? classes.selectedImg : classes.imgPreview}
                    src={images?.length > 0 ? getImageUrl(img?.id) : defaultImage ? defaultImage : '/images/brp-logo.png'}
                    onClick={() => handleUpdateImage(img)}
                    alt="Header"
                    loading="lazy"
                  />
                </Box>
              ))}
            </Box>
          )}
        </>
      }
    />
  );
};

export default ImagesSlideView;
